import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Modal, TextField } from '@heroui/react';
import type { OperationActivityLine } from '@/api/models';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { mirrorLines, type ReverseError } from '../../activityMeta';

type ReverseDialogProps = {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    /** The lines of the entry being reversed. Mirrored for display, never recomputed. */
    lines: OperationActivityLine[];
    isReversing: boolean;
    error: ReverseError | null;
    onConfirm: (reason: string) => void;
};

/**
 * Confirming a reversal.
 *
 * **The dialog renders the exact opposite entry**, line for line, with the two
 * columns swapped — `mirrorLines` is presentation and nothing else, no amount is
 * derived or rounded. That matters because a confirm dialog that showed anything
 * other than what is about to be posted would be worse than no dialog at all.
 *
 * **The reason is required, on both sides.** The button stays disabled until
 * something is typed, and the server refuses a reasonless reversal of a recorded
 * operation anyway (`REASON_REQUIRED`). The client gate is there to spend the
 * round trip rarely, not to be the rule.
 *
 * The sentence under the table is the one thing most likely to be misunderstood
 * about the act: the original entry **stays**. Reversal adds a linked mirror; it
 * does not remove anything, and the Activity register goes on showing both.
 */
export const ReverseDialog: React.FC<ReverseDialogProps> = ({
    isOpen,
    onOpenChange,
    lines,
    isReversing,
    error,
    onConfirm,
}) => {
    const { t } = useTranslation('pages');
    const [reason, setReason] = React.useState('');

    // A reopened dialog never inherits the last attempt's text — the reason
    // belongs to one reversal, and a prefilled one invites confirming somebody
    // else's words.
    React.useEffect(() => {
        if (isOpen) {
            setReason('');
        }
    }, [isOpen]);

    const mirrored = React.useMemo(() => mirrorLines(lines), [lines]);
    const canConfirm = reason.trim().length > 0 && !isReversing;

    return (
        <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-xl"
                        {...getNodeTestIdProp('operations-reverse-dialog')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                {t('operations.activity.reverse.title')}
                            </Modal.Heading>
                        </Modal.Header>

                        <Modal.Body className="flex flex-col gap-4">
                            <p className="text-sm text-muted">
                                {t('operations.activity.reverse.body')}
                            </p>

                            <table className="w-full text-sm">
                                <caption className="sr-only">
                                    {t('operations.activity.reverse.caption')}
                                </caption>
                                <thead>
                                    <tr className="border-b border-separator text-xs uppercase tracking-wide text-muted">
                                        <th
                                            className="py-1 text-start font-medium"
                                            scope="col"
                                        >
                                            {t(
                                                'operations.activity.detail.account',
                                            )}
                                        </th>
                                        <th
                                            className="py-1 text-end font-medium"
                                            scope="col"
                                        >
                                            {t(
                                                'operations.activity.detail.debit',
                                            )}
                                        </th>
                                        <th
                                            className="py-1 text-end font-medium"
                                            scope="col"
                                        >
                                            {t(
                                                'operations.activity.detail.credit',
                                            )}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mirrored.map((line) => (
                                        <tr
                                            className="border-b border-separator/60"
                                            key={`${line.accountId}-${line.debitTetri}-${line.creditTetri}`}
                                            {...getNodeTestIdProp(
                                                `operations-reverse-line-${line.accountId}`,
                                            )}
                                        >
                                            <th
                                                className="py-1.5 text-start font-normal text-foreground"
                                                scope="row"
                                            >
                                                {`${line.code} — ${line.name}`}
                                            </th>
                                            <td className="py-1.5 text-end tabular-nums">
                                                {line.debitTetri > 0
                                                    ? formatGel(line.debitTetri)
                                                    : null}
                                            </td>
                                            <td className="py-1.5 text-end tabular-nums">
                                                {line.creditTetri > 0
                                                    ? formatGel(
                                                          line.creditTetri,
                                                      )
                                                    : null}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <p
                                className="text-xs text-muted"
                                {...getNodeTestIdProp(
                                    'operations-reverse-keeps-original',
                                )}
                            >
                                {t('operations.activity.reverse.keepsOriginal')}
                            </p>

                            <TextField
                                className="flex w-full flex-col gap-1"
                                isDisabled={isReversing}
                                isInvalid={error?.target === 'reason'}
                                isRequired
                                onChange={setReason}
                                value={reason}
                                {...getNodeTestIdProp(
                                    'operations-reverse-reason',
                                )}
                            >
                                <Label className="text-xs text-muted">
                                    {t('operations.activity.reverse.reason')}
                                </Label>
                                <Input
                                    placeholder={t(
                                        'operations.activity.reverse.reasonPlaceholder',
                                    )}
                                />
                            </TextField>

                            {error ? (
                                <p
                                    className="text-sm text-danger"
                                    {...getNodeTestIdProp(
                                        'operations-reverse-error',
                                    )}
                                >
                                    {error.message}
                                </p>
                            ) : null}
                        </Modal.Body>

                        <Modal.Footer>
                            <Button
                                isDisabled={isReversing}
                                onPress={() => onOpenChange(false)}
                                variant="outline"
                                {...getNodeTestIdProp(
                                    'operations-reverse-cancel',
                                )}
                            >
                                {t('operations.activity.reverse.cancel')}
                            </Button>
                            <Button
                                isDisabled={!canConfirm}
                                onPress={() => onConfirm(reason.trim())}
                                variant="primary"
                                {...getNodeTestIdProp(
                                    'operations-reverse-confirm',
                                )}
                            >
                                {isReversing
                                    ? t('operations.activity.reverse.reversing')
                                    : t('operations.activity.reverse.confirm')}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
